import React, { useState, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Select from 'react-select';
import * as dropboxService from '@app/services/dropbox';
import BreadcrumbPath from '../Common/BreadcrumbPath';

interface CopyModalProps {
  item: any;
  type: 'file' | 'folder';
  onClose: () => void;
  onSuccess: () => void;
}

interface FolderOption {
  value: string | null;
  label: string;
  path: string;
}

const CopyModal: React.FC<CopyModalProps> = ({ item, type, onClose, onSuccess }) => {
  const folders = useSelector((state: any) => state.dropbox.folders);
  const [targetFolderId, setTargetFolderId] = useState<string | null>(item.folder_id || item.parent_id || null);
  
  // Generate proper copy name with extension handling
  const generateCopyName = (originalName: string, extension?: string) => {
    if (type === 'folder' || !extension) {
      return `${originalName} (Copy)`;
    }
    
    // For files, check if the name already includes the extension
    const nameWithoutExt = originalName.endsWith(`.${extension}`) 
      ? originalName.slice(0, -extension.length - 1)
      : originalName;
    
    return `${nameWithoutExt} (Copy).${extension}`;
  };
  
  const [newName, setNewName] = useState(generateCopyName(item.name, item.extension));
  const [loading, setLoading] = useState(false);

  // Build folder options with full paths
  const folderOptions = useMemo(() => {
    const options: FolderOption[] = [
      { value: null, label: 'Root Folder', path: '/' }
    ];

    // Filter out the item itself if it's a folder
    const availableFolders = folders.filter((f: any) => 
      !f.is_deleted && f.id !== item.id
    );

    // Sort by path for better organization
    availableFolders.sort((a: any, b: any) => a.path.localeCompare(b.path));

    availableFolders.forEach((folder: any) => {
      options.push({
        value: folder.id,
        label: folder.name,
        path: folder.path || `/${folder.name}`
      });
    });

    return options;
  }, [folders, item.id]);

  // Custom option component with path display
  const formatOptionLabel = (option: FolderOption) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <BreadcrumbPath path={option.path} showIcon={true} />
    </div>
  );

  // Custom filter function for searching by path
  const filterOption = (option: any, inputValue: string) => {
    const searchValue = inputValue.toLowerCase();
    const optionData = option.data as FolderOption;
    return (
      optionData.label.toLowerCase().includes(searchValue) ||
      optionData.path.toLowerCase().includes(searchValue)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (type === 'file') {
        response = await dropboxService.copyFile(item.id, targetFolderId, newName.trim());
      } else {
        response = await dropboxService.copyFolder(item.id, targetFolderId, newName.trim());
      }

      if (response?.success) {
        toast.success(`${type === 'file' ? 'File' : 'Folder'} copied successfully`);
        onSuccess();
        onClose();
      } else {
        toast.error(response?.message || 'Failed to copy');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to copy');
    } finally {
      setLoading(false);
    }
  };

  const selectedOption = folderOptions.find(opt => opt.value === targetFolderId) || folderOptions[0];

  return (
    <Modal show={true} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Copy {type === 'file' ? 'File' : 'Folder'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>New Name</Form.Label>
            <Form.Control
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter new name"
              required
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Destination Folder</Form.Label>
            <Select
              value={selectedOption}
              onChange={(option) => setTargetFolderId(option?.value || null)}
              options={folderOptions}
              formatOptionLabel={formatOptionLabel}
              filterOption={filterOption}
              placeholder="Search or select destination folder..."
              isClearable={false}
              isSearchable={true}
              isDisabled={loading}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px',
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
            <Form.Text className="text-muted">
              Type to search by folder name or path
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading || !newName.trim()}>
            {loading ? 'Copying...' : 'Copy'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CopyModal;
